import { isSpotId } from "./spots";
import type { SpotId } from "./types";

const STORAGE_KEY = "tokyo-toilet-map:pending-arrival:v1";
const SESSION_VERSION = 1 as const;
const SESSION_LIFETIME_MS = 2 * 60 * 60 * 1_000;
const MAX_TOILET_ID_LENGTH = 160;
const MAX_FACILITY_NAME_LENGTH = 120;

export type PendingArrivalSession = {
  version: typeof SESSION_VERSION;
  toiletId: string;
  spotId: SpotId;
  facilityName: string;
  dueAt: string;
  expiresAt: string;
};

export type ScheduleArrivalSessionInput = {
  toiletId: string;
  spotId: SpotId;
  facilityName: string;
  durationSeconds: number;
};

const sanitizeText = (value: unknown, maximumLength: number) =>
  typeof value === "string" ? value.trim().slice(0, maximumLength) : "";

const getStorage = (): Storage | undefined => {
  try {
    // A visit prompt is useful only for the active outing. Session storage
    // survives reload/backgrounding but does not create a durable visit log.
    return globalThis.sessionStorage ?? undefined;
  } catch {
    return undefined;
  }
};

const safeRemove = (storage: Storage) => {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // A disabled or full browser storage must not interrupt the user journey.
  }
};

const dateMilliseconds = (value: string) => {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) ? milliseconds : undefined;
};

const normalizeSession = (value: unknown): PendingArrivalSession | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Partial<PendingArrivalSession>;
  const toiletId = sanitizeText(candidate.toiletId, MAX_TOILET_ID_LENGTH);
  const facilityName = sanitizeText(
    candidate.facilityName,
    MAX_FACILITY_NAME_LENGTH,
  );
  const dueAt = typeof candidate.dueAt === "string" ? candidate.dueAt : "";
  const expiresAt =
    typeof candidate.expiresAt === "string" ? candidate.expiresAt : "";
  const dueMilliseconds = dateMilliseconds(dueAt);
  const expiresMilliseconds = dateMilliseconds(expiresAt);

  if (
    candidate.version !== SESSION_VERSION ||
    !toiletId ||
    toiletId !== candidate.toiletId ||
    !facilityName ||
    facilityName !== candidate.facilityName ||
    typeof candidate.spotId !== "string" ||
    !isSpotId(candidate.spotId) ||
    dueMilliseconds === undefined ||
    expiresMilliseconds === undefined ||
    expiresMilliseconds - dueMilliseconds !== SESSION_LIFETIME_MS
  ) {
    return undefined;
  }

  // Rebuild the object explicitly so unexpected fields can never leave storage.
  return {
    version: SESSION_VERSION,
    toiletId,
    spotId: candidate.spotId,
    facilityName,
    dueAt,
    expiresAt,
  };
};

const readSession = (): PendingArrivalSession | undefined => {
  const storage = getStorage();
  if (!storage) return undefined;
  try {
    const serialized = storage.getItem(STORAGE_KEY);
    if (!serialized) return undefined;
    const session = normalizeSession(JSON.parse(serialized));
    if (!session) safeRemove(storage);
    return session;
  } catch {
    safeRemove(storage);
    return undefined;
  }
};

const resolveNow = (now: Date) => {
  const milliseconds = now.getTime();
  return Number.isFinite(milliseconds) ? milliseconds : undefined;
};

export function scheduleArrivalSession(
  input: ScheduleArrivalSessionInput,
  now: Date = new Date(),
): PendingArrivalSession | undefined {
  const storage = getStorage();
  const nowMilliseconds = resolveNow(now);
  const toiletId = sanitizeText(input?.toiletId, MAX_TOILET_ID_LENGTH);
  const facilityName = sanitizeText(
    input?.facilityName,
    MAX_FACILITY_NAME_LENGTH,
  );

  if (
    !storage ||
    nowMilliseconds === undefined ||
    !input ||
    !toiletId ||
    !facilityName ||
    typeof input.spotId !== "string" ||
    !isSpotId(input.spotId) ||
    !Number.isFinite(input.durationSeconds)
  ) {
    return undefined;
  }

  const dueMilliseconds =
    nowMilliseconds + Math.max(1, input.durationSeconds) * 1_000;
  const expiresMilliseconds = dueMilliseconds + SESSION_LIFETIME_MS;
  if (!Number.isFinite(dueMilliseconds) || !Number.isFinite(expiresMilliseconds)) {
    return undefined;
  }

  let dueAt: string;
  let expiresAt: string;
  try {
    dueAt = new Date(dueMilliseconds).toISOString();
    expiresAt = new Date(expiresMilliseconds).toISOString();
  } catch {
    return undefined;
  }

  const session: PendingArrivalSession = {
    version: SESSION_VERSION,
    toiletId,
    spotId: input.spotId,
    facilityName,
    dueAt,
    expiresAt,
  };

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(session));
    return session;
  } catch {
    return undefined;
  }
}

export function getPendingArrivalSession(
  now: Date = new Date(),
): PendingArrivalSession | undefined {
  const nowMilliseconds = resolveNow(now);
  const session = readSession();
  if (!session || nowMilliseconds === undefined) return undefined;
  const expiresMilliseconds = Date.parse(session.expiresAt);
  if (nowMilliseconds > expiresMilliseconds) {
    const storage = getStorage();
    if (storage) safeRemove(storage);
    return undefined;
  }
  return session;
}

export function getDueArrivalSession(
  now: Date = new Date(),
): PendingArrivalSession | undefined {
  const nowMilliseconds = resolveNow(now);
  const session = getPendingArrivalSession(now);
  if (!session || nowMilliseconds === undefined) return undefined;
  return nowMilliseconds >= Date.parse(session.dueAt) ? session : undefined;
}

export function clearArrivalSession(): void {
  const storage = getStorage();
  if (storage) safeRemove(storage);
}
